// src/utils/map/buildRigFracFilter.ts
//
// Pure builder for the Rig/Frac layer-local attribute filter (EVA-63).
// No React, no store, no Mapbox import — unit-testable in isolation.
import { RIG_FRAC_FIELD } from "constants/rigFrac.constants";
import type { RigFracLayerFilter } from "stores/map/mapTypes";
import { normalizeMatchKey } from "utils/color/focusColorSync/operatorKeys";
import {
  MAIN_FILTER_CONSTRAINT_ATTRIBUTE_ORDER,
  MainFilterRigFracConstraints
} from "utils/map/mainFilterRigFrac";
import { RIG_FRAC_NO_VALUE_OPTION } from "utils/map/rigFracFacets";

/** A Mapbox GL filter expression (kept untyped/opaque here — no mapbox-gl import). */
export type FilterExpression = unknown[];

// Fixed field order so the composed expression is deterministic across calls.
const FIELD_ORDER: readonly (keyof RigFracLayerFilter)[] = [
  "operator",
  "county",
  "basin",
  "subBasin",
  "country",
  "stateProvince",
  "locationType",
  "rigTrajectory"
];

/**
 * Compose a layer's Mapbox filter expression from its selections.
 *
 * The layer's existing rig-vs-frac `type` clause is always kept first inside
 * an `all` (AND) — attribute selections are ANDed on top of it and can never
 * replace or drop it. Dropping the type clause would let rigs/frac crews leak
 * onto the wrong layer (same bug class as the EVA-6899 stick-leak regression).
 *
 * Within a field, multiple selected values are combined with OR via an `in`
 * clause. Across fields, clauses are combined with AND as siblings under the
 * outer `all`. An empty/absent field contributes no clause; when every field
 * is empty the result collapses to `["all", typeClause]`.
 *
 * `extraClauses` are any additional predicates that must AND onto the same
 * layer expression, appended as further siblings under the outer `all`. This
 * is how the single writer owns every rig/frac filtering concern at once:
 *   - the EVA-64 session-only value-hiding exclusion (an exclusion on the
 *     layer's active color-by attribute), and
 *   - the EVA-66 committed-well polygon scoping (a `within` inclusion plus an
 *     exclude-polygon `!within`).
 * Any falsy entry (null/undefined) is skipped, so callers can build the list
 * conditionally without pre-filtering. Order among the extra clauses is
 * irrelevant — AND is commutative.
 */
/**
 * One field's clause: an `in` over the selected values.
 *
 * `rigTrajectory` (EVA-107) needs more than that. map-svc-rs omits the tag
 * entirely for a feature with no trajectory rather than tagging `""`, and
 * `["in", ["get", "rigTrajectory"], …]` evaluates to false on a missing
 * property — so selecting the "n/a" option would match nothing at all instead
 * of the frac crews and unclassified rigs the user is asking for. Those get an
 * `["!", ["has", …]]` predicate, ORed with the real values when both are
 * selected.
 */
function buildFieldClause(
  field: keyof RigFracLayerFilter,
  values: readonly string[]
): FilterExpression {
  if (field !== RIG_FRAC_FIELD.RigTrajectory) {
    return ["in", ["get", field], ["literal", values]];
  }

  const realValues = values.filter((v) => v !== RIG_FRAC_NO_VALUE_OPTION);
  const wantsMissing = realValues.length !== values.length;

  const parts: FilterExpression[] = [];
  if (realValues.length > 0) {
    parts.push(["in", ["get", field], ["literal", realValues]]);
  }
  if (wantsMissing) {
    parts.push(["!", ["has", field]]);
  }
  return parts.length === 1 ? parts[0] : ["any", ...parts];
}

export function buildRigFracFilter(
  typeClause: FilterExpression,
  selections: RigFracLayerFilter,
  extraClauses: Maybe<FilterExpression>[] = []
): FilterExpression {
  const clauses: FilterExpression[] = [];

  for (const field of FIELD_ORDER) {
    const values = selections?.[field];
    if (values && values.length > 0) {
      clauses.push(buildFieldClause(field, values));
    }
  }

  for (const extra of extraClauses ?? []) {
    if (extra) {
      clauses.push(extra);
    }
  }

  return ["all", typeClause, ...clauses];
}

/**
 * EVA-172: AND the main well filter's constraints onto the layer's own
 * selections.
 *
 * Compared case-insensitively via Mapbox's `downcase` against constraint
 * values folded with `normalizeMatchKey`. This is NOT the same rule the
 * colour half (operatorKeys.ts) matches with: normalizeMatchKey also trims
 * and maps a blank value to "n/a", neither of which `downcase` does — see the
 * known-limitation test below. Values here are always non-blank
 * (resolveMainFilterRigFracConstraints filters blanks before populating
 * include/exclude), so normalizeMatchKey's blank -> "n/a" collapse can never
 * fire on this path.
 */
export function buildMainFilterConstraintClauses(
  constraints: Maybe<MainFilterRigFracConstraints>
): FilterExpression[] {
  const clauses: FilterExpression[] = [];
  for (const attribute of MAIN_FILTER_CONSTRAINT_ATTRIBUTE_ORDER) {
    const constraint = constraints?.[attribute];
    if (!constraint) continue;
    // `downcase` asserts its argument is already a string; `get` on a tag
    // absent from the tile yields null, which fails that assertion and makes
    // the whole clause evaluate to false (an exclude that should PASS a
    // missing tag would instead hide it). `to-string` coerces null to "" so
    // downcase never sees a non-string.
    const attributeExpr = ["downcase", ["to-string", ["get", attribute]]];
    if (constraint.include) {
      // An empty include is intentional — "nothing qualifies" — and must
      // still emit a clause rather than being treated as absent.
      clauses.push([
        "in",
        attributeExpr,
        ["literal", constraint.include.map(normalizeMatchKey)]
      ]);
    }
    if (constraint.exclude && constraint.exclude.length > 0) {
      clauses.push([
        "!",
        ["in", attributeExpr, ["literal", constraint.exclude.map(normalizeMatchKey)]]
      ]);
    }
  }
  return clauses;
}
